/**
 * Seedable PRNG-backed die roller.
 *
 * All randomness in src/engine/ must route through this module.
 * Call seedRng(seed) at test setup to make rolls deterministic.
 */

// Mulberry32 — fast 32-bit PRNG with good statistical properties.
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) >>> 0;
    return ((t ^ (t >>> 14)) >>> 0) / 0x100000000;
  };
}

// Seed from crypto on first load so non-test usage is still unpredictable.
const _bootstrapSeed = (() => {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return arr[0]!;
})();

let _rng = mulberry32(_bootstrapSeed);

/** Replace the global RNG with a deterministic seeded instance. Call from test setup. */
export function seedRng(seed: number): void {
  _rng = mulberry32(seed);
}

/** Uniform integer in [min, max] inclusive. Routes all engine randomness through the seeded RNG. */
export function randomInt(min: number, max: number): number {
  return min + Math.floor(_rng() * (max - min + 1));
}

/** Uniform float in [0, 1). Routes through the seeded RNG for determinism. */
export function randomFloat01(): number {
  return _rng();
}

/** Roll a single N-sided die. Returns 1–sides inclusive. */
export function rollDie(sides: number): number {
  if (sides < 2) throw new RangeError(`Die must have at least 2 sides, got ${sides}`);
  return randomInt(1, sides);
}

/** Roll count dice of `sides` and return individual results. */
export function rollDice(count: number, sides: number): number[] {
  return Array.from({ length: count }, () => rollDie(sides));
}

/** Roll a d20 and return the raw result. */
export function rollD20(): number {
  return rollDie(20);
}

/** Roll two d20s and return the higher value (advantage). */
export function rollWithAdvantage(): number {
  return Math.max(rollD20(), rollD20());
}

/** Roll two d20s and return the lower value (disadvantage). */
export function rollWithDisadvantage(): number {
  return Math.min(rollD20(), rollD20());
}

// ── Dice-notation parsing ────────────────────────────────────────────────────

/** Parsed `NdM[+K|-K]` notation. `modifier` is 0 when no modifier is present. */
export interface ParsedDice {
  readonly count: number;
  readonly sides: number;
  readonly modifier: number;
}

/** Thrown when a notation string can't be parsed. */
export class DiceNotationError extends Error {
  constructor(notation: string) {
    super(`Invalid dice notation: "${notation}"`);
    this.name = "DiceNotationError";
  }
}

/**
 * Parse `NdM[+K|-K]` notation. Whitespace is tolerated. Rejects `0dM`,
 * `Nd0`, and `Nd1` — a die must have at least two sides, and at least one
 * die must be rolled.
 */
export function parseDiceNotation(notation: string): ParsedDice {
  const match = notation.replace(/\s+/g, "").match(/^(\d+)d(\d+)([+-]\d+)?$/i);
  if (!match) throw new DiceNotationError(notation);
  const count = Number(match[1]);
  const sides = Number(match[2]);
  const modifier = match[3] ? Number(match[3]) : 0;
  if (count < 1 || sides < 2) throw new DiceNotationError(notation);
  return { count, sides, modifier };
}

/** Parse + roll in one step. Uses the seeded engine RNG by default. */
export function rollDiceNotation(
  notation: string,
  rng: (min: number, max: number) => number = randomInt,
): number {
  const { count, sides, modifier } = parseDiceNotation(notation);
  let sum = modifier;
  for (let i = 0; i < count; i++) sum += rng(1, sides);
  return sum;
}

/**
 * Probability gate. Returns `true` with frequency `chance` (clamped to
 * `[0, 1]`). `chance <= 0` always returns `false`; `chance >= 1` always
 * returns `true`. 4-digit precision supports chances like 0.0125 (1.25%).
 */
export function rollChance(
  chance: number,
  rng: (min: number, max: number) => number = randomInt,
): boolean {
  if (chance <= 0) return false;
  if (chance >= 1) return true;
  const threshold = Math.round(chance * 10_000);
  return rng(0, 9_999) < threshold;
}
